<template>
  <div class="ma-5">
    <v-card class="elevation-3" max-width="760">
      <v-card-title>
        <v-icon left>mdi-lock-clock</v-icon>
        Bloqueio de Acesso Agendado
      </v-card-title>
      <v-card-subtitle>
        A partir da data e hora definidas, somente administradores acessam o sistema.
        Os demais usuários não conseguem entrar e quem estiver logado é desconectado na próxima ação.
      </v-card-subtitle>

      <v-card-text>
        <v-alert :type="status.tipo" text dense class="mb-5">
          {{ status.texto }}
        </v-alert>

        <v-row>
          <v-col cols="12" sm="6" class="py-1">
            <v-text-field
              v-model="data"
              label="Data do bloqueio*"
              type="date"
              outlined
              dense
              prepend-inner-icon="mdi-calendar"
              :error-messages="dataErrors"
            ></v-text-field>
          </v-col>
          <v-col cols="12" sm="6" class="py-1">
            <v-text-field
              v-model="hora"
              label="Hora do bloqueio*"
              type="time"
              outlined
              dense
              prepend-inner-icon="mdi-clock-outline"
              :error-messages="horaErrors"
            ></v-text-field>
          </v-col>
        </v-row>

        <div class="caption grey--text text--darken-1">
          Horário do servidor (America/Manaus): {{ formatar(agoraServidor, true) }}.
          Uma data já passada bloqueia imediatamente.
        </div>

        <v-alert v-if="totalBloqueioTemp > 0" type="info" text dense class="mt-4 mb-0">
          {{ totalBloqueioTemp }} usuário(s) também estão com o bloqueio manual individual ativo
          e continuam sem acesso mesmo sem data agendada.
        </v-alert>
      </v-card-text>

      <v-card-actions class="px-4 pb-4">
        <v-btn
          text
          color="red"
          :disabled="!bloqueioAtual || salvando"
          @click="remover"
        >
          <v-icon left>mdi-lock-open-variant</v-icon>
          Remover bloqueio
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn color="primary" :loading="salvando" @click="salvar">
          <v-icon left>mdi-content-save</v-icon>
          Salvar
        </v-btn>
      </v-card-actions>
    </v-card>

    <v-card class="elevation-3 mt-5" max-width="760">
      <v-card-title>
        <v-icon left>mdi-account-check</v-icon>
        Usuários liberados do bloqueio agendado
      </v-card-title>
      <v-card-subtitle>
        Continuam acessando mesmo após a data e hora do bloqueio.
        Não se aplica ao bloqueio manual individual.
      </v-card-subtitle>

      <v-card-text>
        <v-autocomplete
          v-model="usuarioSelecionado"
          :items="resultadoBusca"
          :loading="buscando"
          :search-input.sync="termoBusca"
          item-value="id"
          item-text="name"
          label="Buscar usuário por nome ou e-mail"
          placeholder="Digite ao menos 3 letras"
          prepend-inner-icon="mdi-magnify"
          no-filter
          hide-no-data
          return-object
          outlined
          dense
          :disabled="salvando"
          @change="adicionarLiberado"
        >
          <template v-slot:item="{ item }">
            <v-list-item-content>
              <v-list-item-title>{{ item.name }}</v-list-item-title>
              <v-list-item-subtitle>{{ item.email }}</v-list-item-subtitle>
            </v-list-item-content>
            <v-list-item-action v-if="item.liberado_bloqueio">
              <v-chip x-small color="green" text-color="white">Já liberado</v-chip>
            </v-list-item-action>
          </template>
        </v-autocomplete>

        <div v-if="!liberados.length" class="body-2 grey--text">
          Nenhum usuário liberado.
        </div>
        <v-list v-else dense class="pa-0">
          <v-list-item v-for="u in liberados" :key="u.id" class="px-0">
            <v-list-item-content>
              <v-list-item-title>{{ u.name }}</v-list-item-title>
              <v-list-item-subtitle>
                {{ u.email }}
                <span v-if="u.bloqueio_temp === 'S'" class="orange--text text--darken-2">
                  · com bloqueio manual ativo, continua sem acesso
                </span>
              </v-list-item-subtitle>
            </v-list-item-content>
            <v-list-item-action>
              <v-btn icon small color="red" title="Remover liberação" :disabled="salvando" @click="removerLiberado(u)">
                <v-icon small>mdi-close</v-icon>
              </v-btn>
            </v-list-item-action>
          </v-list-item>
        </v-list>
      </v-card-text>
    </v-card>
  </div>
</template>
<script>
import BloqueioAPI from "./../../api/bloqueio";

export default {
  name: "CfgBloqueioAcesso",

  data: () => ({
    data: "",
    hora: "",
    tentouSalvar: false,
    salvando: false,
    bloqueioAtual: null,
    bloqueadoAgora: false,
    agoraServidor: null,
    totalBloqueioTemp: 0,
    liberados: [],
    usuarioSelecionado: null,
    termoBusca: null,
    resultadoBusca: [],
    buscando: false,
    timerBusca: null,
  }),

  computed: {
    dataErrors() {
      return this.tentouSalvar && !this.data ? ["Informe a data."] : [];
    },
    horaErrors() {
      return this.tentouSalvar && !this.hora ? ["Informe a hora."] : [];
    },
    status() {
      if (!this.bloqueioAtual) {
        return { tipo: "success", texto: "Sem bloqueio agendado. Todos os usuários acessam normalmente." };
      }
      if (this.bloqueadoAgora) {
        return {
          tipo: "error",
          texto: `Sistema bloqueado desde ${this.formatar(this.bloqueioAtual)}. Somente administradores acessam.`,
        };
      }
      return {
        tipo: "warning",
        texto: `Bloqueio agendado para ${this.formatar(this.bloqueioAtual)}.`,
      };
    },
  },

  watch: {
    // Debounce manual (v-lazy-input quebra o onInput do Vuetify)
    termoBusca(valor) {
      clearTimeout(this.timerBusca);
      const termo = (valor || "").trim();
      if (termo.length < 3 || (this.usuarioSelecionado && termo === this.usuarioSelecionado.name)) {
        this.resultadoBusca = [];
        return;
      }
      this.timerBusca = setTimeout(() => this.buscarUsuarios(termo), 400);
    },
  },

  created() {
    this.carregar();
  },

  methods: {
    // "2026-10-01 14:30[:00]" -> "01/10/2026 14:30[:00]"
    formatar(valor, comSegundos = false) {
      if (!valor) return "-";
      const [d, h] = valor.split(" ");
      const [a, m, dia] = d.split("-");
      return `${dia}/${m}/${a} ${comSegundos ? h : h.substring(0, 5)}`;
    },

    aplicarEstado(estado) {
      if (estado.token) localStorage.setItem("token", estado.token);
      this.bloqueioAtual = estado.bloqueio_acesso_em;
      this.bloqueadoAgora = !!estado.bloqueado_agora;
      this.agoraServidor = estado.agora_servidor;
      this.totalBloqueioTemp = estado.total_bloqueio_temp || 0;
      this.liberados = estado.liberados || [];

      const [d, h] = (estado.bloqueio_acesso_em || " ").split(" ");
      this.data = d || "";
      this.hora = h || "";
      this.tentouSalvar = false;
    },

    tratarErro(error, padrao) {
      const err = error.response && error.response.data && error.response.data.error;
      const msg = err === "sem_permissao"
        ? "Acesso restrito a administradores."
        : err === "data_invalida"
        ? "Data ou hora inválida."
        : err === "usuario_invalido"
        ? "Usuário inválido para liberação."
        : padrao;
      this.Helpers.exibirMensagem(msg, "error", 5000);
    },

    carregar() {
      const headers = this.Helpers.iniciarRequisicao();
      BloqueioAPI.getCfg(headers)
        .then((response) => this.aplicarEstado(response.data))
        .catch((error) => this.tratarErro(error, "Falha ao carregar o bloqueio."))
        .finally(() => this.$store.dispatch("loading/habilitarLoading", false));
    },

    enviar(valor, mensagemOk) {
      this.salvando = true;
      const headers = this.Helpers.getHeaderRequisicao();
      BloqueioAPI.salvarCfg(headers, { bloqueio_acesso_em: valor })
        .then((response) => {
          this.aplicarEstado(response.data);
          this.Helpers.exibirMensagem(mensagemOk, "success", 4000);
        })
        .catch((error) => this.tratarErro(error, "Falha ao salvar o bloqueio."))
        .finally(() => {
          this.salvando = false;
        });
    },

    salvar() {
      this.tentouSalvar = true;
      if (!this.data || !this.hora) {
        this.Helpers.exibirMensagem("Informe a data e a hora do bloqueio.", "error", 4000);
        return;
      }

      const valor = `${this.data} ${this.hora.substring(0, 5)}`;
      const jaPassou = this.agoraServidor && `${valor}:00` <= this.agoraServidor;
      if (jaPassou && !confirm("A data informada já passou: o sistema será bloqueado imediatamente para os não administradores. Confirma?")) {
        return;
      }

      this.enviar(valor, jaPassou ? "Sistema bloqueado." : "Bloqueio agendado.");
    },

    buscarUsuarios(termo) {
      this.buscando = true;
      const headers = this.Helpers.getHeaderRequisicao();
      BloqueioAPI.buscarUsuarios(headers, termo)
        .then((response) => {
          if (response.data.token) localStorage.setItem("token", response.data.token);
          this.resultadoBusca = response.data.usuarios || [];
        })
        .catch((error) => this.tratarErro(error, "Falha ao buscar usuários."))
        .finally(() => {
          this.buscando = false;
        });
    },

    liberar(usuario, liberado, mensagemOk) {
      this.salvando = true;
      const headers = this.Helpers.getHeaderRequisicao();
      BloqueioAPI.liberar(headers, { id: usuario.id, liberado })
        .then((response) => {
          // Preserva a data/hora que o admin possa estar editando
          const { data, hora } = this;
          this.aplicarEstado(response.data);
          this.data = data;
          this.hora = hora;
          this.Helpers.exibirMensagem(mensagemOk, "success", 3000);
        })
        .catch((error) => this.tratarErro(error, "Falha ao atualizar a liberação."))
        .finally(() => {
          this.salvando = false;
        });
    },

    adicionarLiberado(usuario) {
      if (!usuario) return;
      this.$nextTick(() => {
        this.usuarioSelecionado = null;
        this.termoBusca = null;
        this.resultadoBusca = [];
      });
      if (usuario.liberado_bloqueio) {
        this.Helpers.exibirMensagem(`${usuario.name} já está liberado.`, "info", 3000);
        return;
      }
      this.liberar(usuario, true, `${usuario.name} liberado do bloqueio agendado.`);
    },

    removerLiberado(usuario) {
      if (!confirm(`Remover a liberação de ${usuario.name}? Se o bloqueio já estiver vigente, ele perde o acesso na próxima ação.`)) {
        return;
      }
      this.liberar(usuario, false, "Liberação removida.");
    },

    remover() {
      if (!confirm("Remover o bloqueio agendado? Todos os usuários voltam a acessar (exceto os com bloqueio manual individual).")) {
        return;
      }
      this.enviar(null, "Bloqueio removido.");
    },
  },
};
</script>
